import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Table2, Users, Euro, ChefHat, RefreshCw, AlertCircle,
  Volume2, VolumeX, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import TableCard from './LiveTables/TableCard';
import TableDetailView from './LiveTables/TableDetailView';
import ProductSelectorView from './LiveTables/ProductSelectorView';
import CajaModal from './LiveTables/CajaModal';
import CancelTableModal from './LiveTables/CancelTableModal';
import ReceiptTicketModal from './LiveTables/ReceiptTicketModal';
import useSmartPolling from '../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const playAlertSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.setValueAtTime(1174.66, audioContext.currentTime);
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.5);
    }, 150);
  } catch (e) {
    console.log('Audio not supported');
  }
};

const LiveTablesPanel = () => {
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [closingMesa, setClosingMesa] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCajaResumen, setShowCajaResumen] = useState(false);
  const [cajaResumen, setCajaResumen] = useState(null);
  const [cajaHistorial, setCajaHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [cancelingMesa, setCancelingMesa] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  
  // Product selector state
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingOrder, setSendingOrder] = useState(false);

  // Refs avoid useCallback dependency churn that was tearing down the polling
  // interval on every render and producing stale-closure kitchen-ready misses.
  const lastKitchenReadyTablesRef = useRef(new Set());
  const soundEnabledRef = useRef(true);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  // --- Data fetching ---
  const fetchMesas = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/mesas/live`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const currentKitchenReadyTables = new Set(
          data.filter(m => m.has_kitchen_ready).map(m => m.numero)
        );
        const newReadyTables = [...currentKitchenReadyTables].filter(
          num => !lastKitchenReadyTablesRef.current.has(num)
        );
        if (newReadyTables.length > 0 && lastKitchenReadyTablesRef.current.size > 0 && soundEnabledRef.current) {
          playAlertSound();
          toast.success(`Cocina lista en mesa ${newReadyTables.join(', ')}!`, {
            icon: <ChefHat className="text-orange-500" />,
            duration: 5000
          });
        }
        lastKitchenReadyTablesRef.current = currentKitchenReadyTables;
        setMesas(data);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/productos-rapidos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const cats = [];
        const prodsByCat = {};
        data.forEach(item => {
          const catName = item.category?.name?.es || item.category?.id || 'Sin categoria';
          cats.push(catName);
          prodsByCat[catName] = item.products || [];
        });
        setCategories(cats);
        setProducts(prodsByCat);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  const fetchCajaResumen = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/ventas/caja/resumen`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCajaResumen(await response.json());
      }
    } catch (error) {
      console.error('Error fetching caja resumen:', error);
    }
  }, []);

  const fetchCajaHistorial = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/ventas/caja/historial?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCajaHistorial(await response.json());
      }
    } catch (error) {
      console.error('Error fetching caja historial:', error);
    }
  }, []);

  const downloadCajaPdf = async () => {
    setDownloadingPdf(true);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/ventas/caja/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `informe_caja_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF descargado');
      } else {
        toast.error('Error al generar PDF');
      }
    } catch (error) {
      toast.error('Error de conexion');
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCajaResumen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling with Page Visibility (pauses when tablet sleeps / tab hidden).
  useSmartPolling(fetchMesas, 10000);

  // --- Helpers ---
  const getProductName = (product) => {
    if (!product?.name) return 'Producto';
    if (typeof product.name === 'string') return product.name;
    return product.name.es || product.name.en || 'Producto';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // --- Cart ---
  const addToCart = (product) => {
    const productName = typeof product.name === 'string' ? product.name : getProductName(product);
    const cartId = product.optionsText ? `${product.id}__${product.optionsText}` : product.id;
    
    setCart(prev => {
      const existing = prev.find(item => item.cartId === cartId);
      if (existing) {
        return prev.map(item =>
          item.cartId === cartId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, name: productName, cartId, quantity: 1 }];
    });
    toast.success(`+ ${productName}`, { duration: 600 });
  };

  const updateQuantity = (cartId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.cartId === cartId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const clearCart = () => setCart([]);
  const getCartTotal = () => cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const getCartCount = () => cart.reduce((sum, item) => sum + item.quantity, 0);

  // --- Actions ---
  const sendOrder = async () => {
    if (!selectedMesa || cart.length === 0) return;
    setSendingOrder(true);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/pedido-manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mesa_numero: selectedMesa.numero,
          items: cart.map(item => ({
            product_id: item.id,
            product_name: item.optionsText ? `${item.name} (${item.optionsText})` : item.name,
            quantity: item.quantity,
            price: item.price,
            options: item.selectedOptions || []
          })),
          notes: ''
        })
      });
      if (response.ok) {
        toast.success(`Pedido enviado a mesa ${selectedMesa.numero}`);
        clearCart();
        setViewMode('grid');
        setSelectedMesa(null);
        fetchMesas();
      } else {
        toast.error('Error al enviar pedido');
      }
    } catch (error) {
      toast.error('Error de conexion');
    } finally {
      setSendingOrder(false);
    }
  };

  const handleCloseMesa = async (paymentMethod) => {
    if (!selectedMesa) return;
    setClosingMesa(selectedMesa.numero);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/mesa/${selectedMesa.numero}/session/close?payment_method=${paymentMethod}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Mesa ${selectedMesa.numero} cerrada (${paymentMethod === 'efectivo' ? 'Efectivo' : 'Tarjeta'}). Total: ${data.total?.toFixed(2)}€`);
        setShowPaymentModal(false);
        // Show receipt ticket
        setReceiptData({
          sale_id: data.sale_id,
          mesa_numero: data.mesa_numero,
          total: data.total,
          items: data.items || [],
          payment_method: data.payment_method,
          closed_at: data.closed_at
        });
        fetchMesas();
        fetchCajaResumen();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al cerrar la mesa');
      }
    } catch (error) {
      toast.error('Error al cerrar la mesa');
    } finally {
      setClosingMesa(null);
    }
  };

  const handleCancelMesa = async (mesaNumero) => {
    setCancelingMesa(mesaNumero);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/mesa/${mesaNumero}/session/close?payment_method=cancelado`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`Mesa ${mesaNumero} cancelada`);
        setShowCancelConfirm(null);
        fetchMesas();
        fetchCajaResumen();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al cancelar la mesa');
      }
    } catch (error) {
      toast.error('Error al cancelar la mesa');
    } finally {
      setCancelingMesa(null);
    }
  };

  const handleMesaClick = (mesa) => {
    setSelectedMesa(mesa);
    setCart([]);
    if (mesa.status === 'ocupada') {
      setViewMode('detail');
    } else {
      setViewMode('addProducts');
    }
  };

  // --- Stats ---
  const mesasOcupadas = mesas.filter(m => m.status === 'ocupada').length;
  const mesasConAlertas = mesas.filter(m => m.has_kitchen_ready).length;
  const totalActivo = mesas.reduce((sum, m) => sum + (m.session?.total || 0), 0);

  // --- Loading ---
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  // --- Receipt Ticket (renders on top of everything) ---
  const receiptModal = receiptData ? (
    <ReceiptTicketModal
      saleData={receiptData}
      onClose={() => {
        setReceiptData(null);
        setSelectedMesa(null);
        setViewMode('grid');
      }}
    />
  ) : null;

  // --- Product Selector View ---
  if (viewMode === 'addProducts' && selectedMesa) {
    return (
      <>
        <ProductSelectorView
        mesa={selectedMesa}
        categories={categories}
        products={products}
        cart={cart}
        searchQuery={searchQuery}
        sendingOrder={sendingOrder}
        onBack={() => { setViewMode('grid'); setSelectedMesa(null); clearCart(); }}
        onSearchChange={setSearchQuery}
        onAddToCart={addToCart}
        onUpdateQuantity={updateQuantity}
        onClearCart={clearCart}
        onSendOrder={sendOrder}
        getProductName={getProductName}
        getCartTotal={getCartTotal}
        getCartCount={getCartCount}
      />
      {receiptModal}
      </>
    );
  }

  // --- Detail View ---
  if (viewMode === 'detail' && selectedMesa && selectedMesa.session) {
    return (
      <>
        <TableDetailView
          mesa={selectedMesa}
          onBack={() => { setViewMode('grid'); setSelectedMesa(null); }}
          onAddProducts={() => { setViewMode('addProducts'); setCart([]); }}
          onShowPayment={() => setShowPaymentModal(true)}
          formatDuration={formatDuration}
          showPaymentModal={showPaymentModal}
          onClosePaymentModal={() => setShowPaymentModal(false)}
          onCloseMesa={handleCloseMesa}
          closingMesa={closingMesa}
        />
        {receiptModal}
      </>
    );
  }

  // --- Grid View (Main) ---
  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="live-tables-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Mesas en Vivo</h2>
          <p className="text-sm text-gray-500 hidden md:block">Toca una mesa para gestionar pedidos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => { fetchCajaResumen(); setShowCajaResumen(true); }} 
            variant="outline" 
            size="sm"
            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
            data-testid="caja-btn"
          >
            <Receipt size={16} className="mr-1" />
            <span className="hidden sm:inline">Caja</span>
          </Button>
          <Button 
            onClick={() => setSoundEnabled(!soundEnabled)} 
            variant="outline" 
            size="sm"
            className={soundEnabled ? 'text-green-600 border-green-300' : 'text-gray-400'}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </Button>
          <Button onClick={fetchMesas} variant="outline" size="sm" className="flex items-center gap-2">
            <RefreshCw size={16} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
        </div>
      </div>

      {/* Caja Modal */}
      {showCajaResumen && cajaResumen && (
        <CajaModal
          cajaResumen={cajaResumen}
          cajaHistorial={cajaHistorial}
          showHistorial={showHistorial}
          downloadingPdf={downloadingPdf}
          onToggleHistorial={() => { 
            if (!showHistorial) fetchCajaHistorial(); 
            setShowHistorial(!showHistorial); 
          }}
          onDownloadPdf={downloadCajaPdf}
          onClose={() => { setShowCajaResumen(false); setShowHistorial(false); }}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 md:gap-4">
        <div className="bg-white rounded-xl p-3 shadow-sm border text-center">
          <Table2 className="text-blue-600 mx-auto mb-1" size={20} />
          <p className="text-2xl font-bold text-gray-900">{mesas.length}</p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm border text-center">
          <Users className="text-green-600 mx-auto mb-1" size={20} />
          <p className="text-2xl font-bold text-gray-900">{mesasOcupadas}</p>
          <p className="text-xs text-gray-500">Ocupadas</p>
        </div>
        <div className={`rounded-xl p-3 shadow-sm border text-center ${mesasConAlertas > 0 ? 'bg-orange-50 border-orange-300 animate-pulse' : 'bg-white'}`}>
          <ChefHat className={mesasConAlertas > 0 ? 'text-orange-600 mx-auto mb-1' : 'text-orange-400 mx-auto mb-1'} size={20} />
          <p className={`text-2xl font-bold ${mesasConAlertas > 0 ? 'text-orange-600' : 'text-gray-900'}`}>{mesasConAlertas}</p>
          <p className={`text-xs ${mesasConAlertas > 0 ? 'text-orange-600' : 'text-gray-500'}`}>Cocina Lista</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm border text-center">
          <Euro className="text-amber-600 mx-auto mb-1" size={20} />
          <p className="text-2xl font-bold text-gray-900">{totalActivo.toFixed(0)}€</p>
          <p className="text-xs text-gray-500">Activo</p>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {mesas.map((mesa) => (
          <TableCard
            key={mesa.numero}
            mesa={mesa}
            onClick={handleMesaClick}
            onCancelClick={setShowCancelConfirm}
            formatDuration={formatDuration}
          />
        ))}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <CancelTableModal
          mesa={showCancelConfirm}
          cancelingMesa={cancelingMesa}
          onConfirm={handleCancelMesa}
          onCancel={() => setShowCancelConfirm(null)}
          formatDuration={formatDuration}
        />
      )}

      {mesas.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay mesas configuradas</h3>
            <p className="text-gray-500">Ve a "Gestion de Mesas" para anadir mesas</p>
          </CardContent>
        </Card>
      )}

      <style>{`
        .kitchen-alert-glow {
          animation: kitchenGlow 1.5s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(249, 115, 22, 0.4);
        }
        @keyframes kitchenGlow {
          0%, 100% { box-shadow: 0 0 15px rgba(249, 115, 22, 0.3); border-color: rgb(251, 146, 60); }
          50% { box-shadow: 0 0 30px rgba(249, 115, 22, 0.6); border-color: rgb(249, 115, 22); }
        }
      `}</style>

      {/* Receipt Ticket Modal */}
      {receiptModal}
    </div>
  );
};

export default LiveTablesPanel;
